import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ExternalLink, ShieldCheck, Loader2 } from "lucide-react";

/**
 * Modal Article 50 AI Act - bloque la premiere connexion tant que
 * l'utilisateur n'a pas coche "J'accepte la nouvelle version des CGU".
 *
 * Actif uniquement si :
 *  - user connecte
 *  - user.cgu_up_to_date === false
 *  - pas en callback OAuth Google (hash session_id=)
 *
 * Ne modifie pas le layout : overlay 100vh + backdrop opaque + carte centrale.
 */
export default function CGUReAcceptModal() {
  const { user, token, refreshMe, api } = useAuth();
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Ne pas afficher si :
  // - pas de user connecte
  // - pas de token (pas encore login)
  // - user est deja a jour
  // - on est en callback OAuth (evite le flash)
  if (!user || !token) return null;
  if (user.cgu_up_to_date) return null;
  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) return null;
  // Le champ n'est pas encore charge (initial login peut ne pas contenir cgu_*)
  if (user.cgu_up_to_date === undefined && !user.current_cgu_version) return null;

  const handleAccept = async () => {
    if (!accepted) {
      toast.error("Coche la case pour accepter la nouvelle version.");
      return;
    }
    setSubmitting(true);
    try {
      await api().post("/auth/accept-cgu");
      await refreshMe();
      toast.success("Merci ! CGU acceptees.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible d'enregistrer ton acceptation. Reessaie.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8 overflow-y-auto"
      style={{ background: "rgba(11, 30, 58, 0.85)", backdropFilter: "blur(4px)" }}
      data-testid="cgu-reaccept-modal"
    >
      <div
        className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
        style={{ borderTop: "4px solid #C9A86A" }}
      >
        {/* Header */}
        <div className="p-6 border-b" style={{ borderColor: "#F0EBE0" }}>
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: "#FFF8E7" }}
            >
              <ShieldCheck className="w-5 h-5" style={{ color: "#C9A86A" }} />
            </div>
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: "#C9A86A" }}>
                Mise a jour importante
              </p>
              <h2
                className="text-lg font-bold"
                style={{ color: "#0F2540", fontFamily: "'Fraunces', serif" }}
              >
                Nouvelles Conditions Generales d'Utilisation
              </h2>
            </div>
          </div>
          <p className="text-sm leading-relaxed mt-3" style={{ color: "#3D4B63" }}>
            Nous avons mis a jour nos CGU pour se conformer au{" "}
            <strong style={{ color: "#0F2540" }}>Reglement UE 2024/1689 sur l'IA (AI Act)</strong>.
            Voici les 3 points essentiels :
          </p>
        </div>

        {/* Body - 3 points */}
        <div className="p-6 space-y-4">
          <PointItem number="1" title="Marquage des contenus generes par IA (Article 50)">
            Chaque reponse produite par nos 12 agents (Sonnet, Opus, Haiku) et chaque analyse
            visuelle est desormais signalee par un badge <em>"Genere par IA"</em>. Rien n'est plus
            ambigu : tu sais quand une IA parle vs un humain.
          </PointItem>
          <PointItem number="2" title="Connexion Google Sign-In (facultative)">
            Tu peux desormais te connecter en 1 clic avec Google. Nous recevons uniquement ton email
            verifie, ton nom et ta photo publique. Aucune creation de compte automatique : tu dois
            deja etre inscrit avec cet email.
          </PointItem>
          <PointItem number="3" title="Sous-traitants transparents">
            Nous listons desormais nommement tous nos sous-traitants (Anthropic, OpenAI, Emergent,
            MongoDB, Resend, Google, ActiveCampaign, Chatbase, Tavily). Tu sais exactement ou
            transitent tes donnees.
          </PointItem>
        </div>

        {/* Link to full CGU */}
        <div className="px-6 pb-4">
          <a
            href="/cgu.html"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cgu-full-link"
            className="inline-flex items-center gap-1.5 text-sm font-semibold hover:underline"
            style={{ color: "#C9A86A" }}
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Lire la version integrale des CGU
          </a>
        </div>

        {/* Checkbox + CTA */}
        <div className="p-6 border-t" style={{ borderColor: "#F0EBE0", background: "#FAF7F1" }}>
          <label className="flex items-start gap-3 cursor-pointer mb-4">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              data-testid="cgu-accept-checkbox"
              className="mt-0.5 w-4 h-4 rounded cursor-pointer"
              style={{ accentColor: "#C9A86A" }}
            />
            <span className="text-sm leading-relaxed" style={{ color: "#0F2540" }}>
              <strong>J'accepte la nouvelle version des CGU</strong> (v{user.current_cgu_version || "1.1"})
              et j'ai pris connaissance des 3 points ci-dessus.
            </span>
          </label>
          <Button
            onClick={handleAccept}
            disabled={!accepted || submitting}
            data-testid="cgu-accept-btn"
            className="w-full h-11 font-semibold text-sm"
            style={{
              background: accepted && !submitting ? "#0F2540" : "#B9C2CE",
              color: "#FAF7F1",
            }}
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Enregistrement...
              </>
            ) : (
              "J'accepte et j'acces a la plateforme"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

function PointItem({ number, title, children }) {
  return (
    <div className="flex gap-3">
      <div
        className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold"
        style={{ background: "#C9A86A" }}
      >
        {number}
      </div>
      <div className="flex-1">
        <h3 className="text-sm font-semibold mb-1" style={{ color: "#0F2540" }}>
          {title}
        </h3>
        <p className="text-xs leading-relaxed" style={{ color: "#3D4B63" }}>
          {children}
        </p>
      </div>
    </div>
  );
}
